import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useGlobalFilesStore } from '../../store/providers/GlobalFiles';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './GlobalFileListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const FileDisplayModal = React.lazy(() => import("../../components/Modals/FileDisplayModal/FileDisplayModal"));
const GlobalFileUploadModal = React.lazy(() => import("../../components/Modals/GlobalFileUploadModal/GlobalFileUploadModal"));

function GlobalFiles() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, newRecord, data, clearData } = useGlobalFilesStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isViewing, setIsViewing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [selectedFile, setSelectedFile] = useState();

    const pagePermission = constants.PERMISSION_CONTENT_DOCUMENTS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (globalFile) => {
        setIsViewing(true);
        setIsNew(false);
        setSelectedFile(globalFile);       
    };

    const addedHandler = () => {
        setIsViewing(false);
        setIsNew(true);
        newRecord();
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const deletedHandler = (globalFile) => {
        deleteRecord({ globalFileId: globalFile.id });
    };

    const cancelledHandler = () => {
        setIsViewing(false);
        setIsNew(false);
    };

    const submittedHandler = () => {
        setIsViewing(false);
        setIsNew(false);
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {            
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'globalFiles', parent: 'Content', text: 'Documents', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="content" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        { isViewing && 
                            <>
                            <FileDisplayModal
                                show={isViewing}
                                canEdit={canEdit}
                                fileUrl={selectedFile.fileUrl}
                                ext={selectedFile.extension}
                                title={selectedFile.name}                    
                                cancelled={cancelledHandler} />  
                                </>          
                        }
                        {
                            isNew && <GlobalFileUploadModal
                                show={isNew}
                                canEdit={canEdit}
                                title="Add File"
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />

                        }

                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Documents" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Document"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(globalFile) => selectedHandler(globalFile)}
                                        deleted={(globalFile) => deletedHandler(globalFile)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(GlobalFiles));
